import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Async, Dispatchers, toAsyncFetched } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { Actions } from '@client/new/helpers/types';
import {
  useProfileModel,
  checkCustomUrlAvailability,
} from '../model/Profile.model';
import { useSaveProfileModel } from '../model/SaveProfile.model';
import {
  ProfileDomain,
  SectionCompletion,
  SectionId,
} from '../model/Profile.types';

export type { SectionCompletion } from '../model/Profile.types';
import {
  profileBaseSchema,
  profileLiveSchema,
  ProfileFormValues,
} from './Profile.schema';
import { calculateAllCompletions } from './Profile.completion';
import { useAiAssistVM, AiAssistVM } from './AiAssist.viewModel';

type ProfileVMActionMap = {
  Save: null;
};

type SaveState = { type: 'idle' } | { type: 'saving' };

type CustomUrlState = {
  value: string;
  isEditable: boolean;
  validate: (url: string) => Promise<[boolean, string?]>;
  onChange: (value: string) => void;
};

type ProfileVMData = {
  profile: ProfileDomain;
  isLive: boolean;
  isSaving: boolean;
  completions: Record<SectionId, SectionCompletion>;
  formValues: ProfileFormValues;
  aiAssist: AiAssistVM;
  customUrl: CustomUrlState;
  form: {
    register: ReturnType<typeof useForm<ProfileFormValues>>['register'];
    errors: ReturnType<
      typeof useForm<ProfileFormValues>
    >['formState']['errors'];
    isDirty: boolean;
    handleSubmit: ReturnType<typeof useForm<ProfileFormValues>>['handleSubmit'];
    setValue: ReturnType<typeof useForm<ProfileFormValues>>['setValue'];
    watch: ReturnType<typeof useForm<ProfileFormValues>>['watch'];
    control: ReturnType<typeof useForm<ProfileFormValues>>['control'];
  };
};

export type ProfileVM = ProfileVMData & Dispatchers<ProfileVMActionMap>;

const toFormDefaults = (profile: ProfileDomain): ProfileFormValues => ({
  firstName: profile.firstName,
  lastName: profile.lastName,
  title: profile.title,
  gender: profile.gender,
  customColorSchema: profile.customColorSchema,
  profileInstagram: profile.profileInstagram,
  profileTiktok: profile.profileTiktok,
  profileYoutubeChannel: profile.profileYoutubeChannel,
  messageProfile: profile.messageProfile,
  accomplishments: profile.accomplishments,
  mediaAvatar: profile.mediaAvatar as ProfileFormValues['mediaAvatar'],
  mediaCover: profile.mediaCover as ProfileFormValues['mediaCover'],
  mediaBanner: profile.mediaBanner as ProfileFormValues['mediaBanner'],
  mediaWelcome: profile.mediaWelcome as ProfileFormValues['mediaWelcome'],
  keywords: profile.keywords,
});

const useCustomUrl = (profile: ProfileDomain | null) => {
  const originalUrl = profile?.customURL ?? '';
  const [customURL, setCustomURL] = useState(originalUrl);
  const requestIdRef = useRef(0);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (originalUrl && !initializedRef.current) {
      setCustomURL(originalUrl);
      initializedRef.current = true;
    }
  }, [originalUrl]);

  const isEditable =
    !!profile && profile.mode !== 'Live' && profile.mode !== 'RunOff';

  const handleChange = useCallback((value: string) => {
    if (value.length > 30) return;
    if (/^[a-zA-Z0-9-_]*$/.test(value)) {
      setCustomURL(value);
    }
  }, []);

  const validate = useCallback(
    async (url: string): Promise<[boolean, string?]> => {
      const thisRequestId = ++requestIdRef.current;
      const isServerAvailable = await checkCustomUrlAvailability(url);

      if (thisRequestId !== requestIdRef.current) return [true];

      const isAvailable = isServerAvailable || url === originalUrl;
      return [
        isAvailable,
        isAvailable ? '' : 'This name is already in use. Please change it.',
      ];
    },
    [originalUrl],
  );

  return { value: customURL, isEditable, validate, onChange: handleChange };
};

export const useProfileVM = (creatorId: number): Async<ProfileVM> => {
  const model = useProfileModel(creatorId);
  const [saveState, setSaveState] = useState<SaveState>({ type: 'idle' });
  const aiAssist = useAiAssistVM(creatorId);

  const profileData = model.type === 'fetched' ? model.value.profile : null;
  const customUrl = useCustomUrl(profileData);

  const isLive =
    model.type === 'fetched' && model.value.profile.mode === 'Live';
  const schema = isLive ? profileLiveSchema : profileBaseSchema;

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    values:
      model.type === 'fetched'
        ? toFormDefaults(model.value.profile)
        : undefined,
  });

  const originalCustomURL =
    model.type === 'fetched' ? model.value.profile.customURL : '';

  const { saveProfile } = useSaveProfileModel({
    creatorId,
    customURL: customUrl.value,
    originalCustomURL,
  });

  const watchedValues = form.watch();

  const completions = useMemo(
    () => calculateAllCompletions(watchedValues),
    [watchedValues],
  );

  if (model.type !== 'fetched') return model;

  const _dispatch = (action: Actions<ProfileVMActionMap>) => {
    switch (action.type) {
      case 'Save':
        if (saveState.type === 'saving') return;

        form.handleSubmit(async (values) => {
          setSaveState({ type: 'saving' });
          try {
            await saveProfile(values);
            toast.success('Profile saved!');
            form.reset(values);
          } catch (err) {
            const message =
              err instanceof Error ? err.message : 'Error saving profile';
            toast.error(message);
          } finally {
            setSaveState({ type: 'idle' });
          }
        })();
        break;
    }
  };

  return toAsyncFetched({
    profile: model.value.profile,
    isLive,
    isSaving: saveState.type === 'saving',
    completions,
    formValues: watchedValues as ProfileFormValues,
    aiAssist,
    customUrl,
    form: {
      register: form.register,
      errors: form.formState.errors,
      isDirty: form.formState.isDirty,
      handleSubmit: form.handleSubmit,
      setValue: form.setValue,
      watch: form.watch,
      control: form.control,
    },
    ...getDispatchers<ProfileVMActionMap>(_dispatch),
  });
};
